// Dok 06 Tidplansgranskning: Bas-P granskar tidplanen och pekar ut krockande arbeten.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DocumentHeader from "./DocumentHeader";
import ScheduleCompare, {
  type DetectedConflict,
  type PlannedActivity,
} from "./ScheduleCompare";
import { importScheduleActivities, mergeActivities } from "@/lib/basProcess/scheduleImport";
import StageTagging from "./StageTagging";
import { CalendarClock, Plus, Trash2 } from "lucide-react";

interface Props {
  projectId: string;
}

interface ConflictRow {
  id: string;
  activities: string;
  period: string;
  place: string;
  risk: string;
  measure: string;
  responsible: string;
  severity: "låg" | "medel" | "hög";
  resolved: boolean;
}

interface Content {
  scheduleSource?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  assessment?: string;
  activities?: PlannedActivity[];
  conflicts?: ConflictRow[];
}

interface Review {
  id: string;
  name: string;
  status: string;
  content_json: Content;
}

const newRow = (): ConflictRow => ({
  id: crypto.randomUUID(),
  activities: "",
  period: "",
  place: "",
  risk: "",
  measure: "",
  responsible: "",
  severity: "medel",
  resolved: false,
});

const ScheduleReview: React.FC<Props> = ({ projectId }) => {
  const [review, setReview] = useState<Review | null>(null);
  const [loading, setLoading] = useState(true);
  const [stages, setStages] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("schedule_reviews")
      .select("id, name, status, content_json")
      .eq("project_id", projectId)
      .order("created_at", { ascending: false })
      .limit(1);
    if (error) toast.error("Kunde inte läsa tidplansgranskningen");
    const row = (data || [])[0] as unknown as Review | undefined;
    setReview(row ? { ...row, content_json: row.content_json || {} } : null);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let active = true;
    void supabase
      .from("project_stages")
      .select("name")
      .eq("project_id", projectId)
      .order("sort_order", { ascending: true })
      .then(({ data }) => {
        if (active) setStages(((data || []) as Array<{ name: string }>).map((s) => s.name));
      });
    return () => {
      active = false;
    };
  }, [projectId]);

  const create = async () => {
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase.from("schedule_reviews").insert({
      project_id: projectId,
      created_by: auth.user?.id ?? null,
      content_json: { conflicts: [newRow()] } as never,
    });
    if (error) return toast.error("Kunde inte skapa granskningen");
    load();
  };

  const save = async (patch: Partial<Review>) => {
    if (!review) return;
    setReview({ ...review, ...patch });
    const { error } = await supabase
      .from("schedule_reviews")
      .update(patch as never)
      .eq("id", review.id);
    if (error) toast.error("Kunde inte spara");
  };

  const setContent = (content: Content) => save({ content_json: content });

  /** Hämtar etapper, arbetsberedningar och arbetsområden som färdiga rader. */
  const runImport = async () => {
    if (!review) return;
    setImporting(true);
    try {
      const { activities, stages: stageNames } = await importScheduleActivities(projectId);
      setStages(stageNames);
      const before = review.content_json.activities || [];
      const merged = mergeActivities(before, activities);
      const added = merged.length - before.length;
      setContent({ ...review.content_json, activities: merged });
      toast.success(
        added > 0
          ? `${added} aktiviteter hämtade – justera datum och utrymmen vid behov`
          : "Inget nytt att hämta"
      );
    } catch {
      toast.error("Kunde inte hämta aktiviteter från projektet");
    } finally {
      setImporting(false);
    }
  };

  /** Lägger krocken som punkt i senaste samordningsmötet (Dok 23). */
  const createCoordinationPoint = async (c: DetectedConflict) => {
    const text = `${c.a.name || "Aktivitet"} × ${c.b.name || "Aktivitet"} i ${
      c.a.area || "okänt utrymme"
    } ${c.from}–${c.to}: ${c.reason}`;
    const { data: auth } = await supabase.auth.getUser();
    const { data } = await supabase
      .from("coordination_meetings")
      .select("id, content_json")
      .eq("project_id", projectId)
      .order("meeting_date", { ascending: false })
      .limit(1);
    const meeting = (data || [])[0] as { id: string; content_json: Record<string, string> } | undefined;

    if (meeting) {
      const current = (meeting.content_json || {}) as Record<string, string>;
      const existing = current.samordning_ue || "";
      if (existing.includes(text)) return toast.info("Punkten finns redan i samordningsmötet");
      const { error } = await supabase
        .from("coordination_meetings")
        .update({
          content_json: {
            ...current,
            samordning_ue: existing ? `${existing}\n• ${text}` : `• ${text}`,
          } as never,
        })
        .eq("id", meeting.id);
      if (error) return toast.error("Kunde inte lägga till punkten");
      return toast.success("Punkten lades till i senaste samordningsmötet");
    }

    const { error } = await supabase.from("coordination_meetings").insert({
      project_id: projectId,
      created_by: auth.user?.id ?? null,
      content_json: { samordning_ue: `• ${text}` } as never,
    });
    if (error) return toast.error("Kunde inte skapa samordningsmöte");
    toast.success("Nytt samordningsmöte skapat med krocken som punkt");
  };

  if (loading) return <p className="py-10 text-center text-muted-foreground">Laddar…</p>;

  const conflicts = review?.content_json.conflicts || [];
  const open = conflicts.filter((c) => !c.resolved);

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="06"
        icon={CalendarClock}
        title="Tidplansgranskning"
        description="Bas-P granskar tidplanen: räcker tiden, och vilka arbeten krockar i tid och rum? Krockar som inte löses i planeringen blir olyckor på bygget."
        legalRef="AFS 2023:3 kap. 4"
        meta={
          review ? (
            <Badge variant={open.length ? "destructive" : "secondary"}>
              {conflicts.length} krockar · {open.length} olösta
            </Badge>
          ) : undefined
        }
        actions={
          review ? undefined : (
            <Button onClick={create}>
              <Plus className="mr-1 h-4 w-4" /> Starta granskning
            </Button>
          )
        }
      />

      {!review && (
        <Card>
          <CardContent className="space-y-3 py-12 text-center">
            <CalendarClock className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Ingen tidplansgranskning gjord ännu.
            </p>
            <Button onClick={create}>Starta granskning</Button>
          </CardContent>
        </Card>
      )}

      {review && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Underlag och bedömning</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Granskad tidplan (namn/version)</Label>
                <Input
                  value={review.content_json.scheduleSource || ""}
                  onChange={(e) =>
                    setContent({ ...review.content_json, scheduleSource: e.target.value })
                  }
                  placeholder="T.ex. Produktionstidplan rev C"
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={review.status} onValueChange={(v) => save({ status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Pågår</SelectItem>
                    <SelectItem value="review">Klar för genomgång</SelectItem>
                    <SelectItem value="approved">Godkänd</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Granskad av</Label>
                <Input
                  value={review.content_json.reviewedBy || ""}
                  onChange={(e) =>
                    setContent({ ...review.content_json, reviewedBy: e.target.value })
                  }
                  placeholder="Namn"
                />
              </div>
              <div>
                <Label>Datum</Label>
                <Input
                  type="date"
                  value={review.content_json.reviewedAt || ""}
                  onChange={(e) =>
                    setContent({ ...review.content_json, reviewedAt: e.target.value })
                  }
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Samlad bedömning – är tiderna realistiska?</Label>
                <Textarea
                  rows={3}
                  value={review.content_json.assessment || ""}
                  onChange={(e) =>
                    setContent({ ...review.content_json, assessment: e.target.value })
                  }
                  placeholder="T.ex. Stomresning och installation ligger parallellt vecka 12–15, tiden för rivning är underskattad."
                />
              </div>
            </CardContent>
          </Card>

          <StageTagging projectId={projectId} onChanged={runImport} />

          <ScheduleCompare
            activities={review.content_json.activities || []}
            stages={stages}
            onImport={runImport}
            importing={importing}
            onCreateCoordinationPoint={createCoordinationPoint}
            onChange={(rows) => setContent({ ...review.content_json, activities: rows })}
            onAddConflict={(c) => {
              const exists = conflicts.some(
                (x) => x.activities === `${c.a.name} × ${c.b.name}` && x.place === c.a.area
              );
              if (exists) return toast.info("Krocken finns redan i listan");
              setContent({
                ...review.content_json,
                conflicts: [
                  ...conflicts,
                  {
                    ...newRow(),
                    activities: `${c.a.name} × ${c.b.name}`,
                    period: `${c.from} – ${c.to}`,
                    place: c.a.area,
                    risk: c.reason,
                    responsible: [c.a.actor, c.b.actor].filter(Boolean).join(" / "),
                    severity: c.severity,
                  },
                ],
              });
              toast.success("Krocken lades till");
            }}
          />


          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Krockande arbeten</CardTitle>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setContent({ ...review.content_json, conflicts: [...conflicts, newRow()] })
                }
              >
                <Plus className="mr-1 h-4 w-4" /> Lägg till krock
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {conflicts.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Inga krockar registrerade. Lägg till de arbeten som pågår samtidigt på samma
                  plats.
                </p>
              )}
              {conflicts.map((c) => {
                const patch = (p: Partial<ConflictRow>) =>
                  setContent({
                    ...review.content_json,
                    conflicts: conflicts.map((x) => (x.id === c.id ? { ...x, ...p } : x)),
                  });
                return (
                  <div key={c.id} className="grid gap-3 rounded-md border p-3 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <Label>Vilka arbeten krockar?</Label>
                      <Input
                        value={c.activities}
                        onChange={(e) => patch({ activities: e.target.value })}
                        placeholder="T.ex. lyft med mobilkran och stomkomplettering"
                      />
                    </div>
                    <div>
                      <Label>När</Label>
                      <Input
                        value={c.period}
                        onChange={(e) => patch({ period: e.target.value })}
                        placeholder="Vecka 12–15"
                      />
                    </div>
                    <div>
                      <Label>Var</Label>
                      <Input
                        value={c.place}
                        onChange={(e) => patch({ place: e.target.value })}
                        placeholder="Plan 3, norra gaveln"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label>Risk som uppstår</Label>
                      <Textarea
                        rows={2}
                        value={c.risk}
                        onChange={(e) => patch({ risk: e.target.value })}
                        placeholder="T.ex. fallande last över arbetsplats med pågående montage"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label>Åtgärd i tidplan eller på plats</Label>
                      <Textarea
                        rows={2}
                        value={c.measure}
                        onChange={(e) => patch({ measure: e.target.value })}
                        placeholder="T.ex. lyft förläggs till morgon före montagestart, avspärrat lyftområde"
                      />
                    </div>
                    <div>
                      <Label>Ansvarig</Label>
                      <Input
                        value={c.responsible}
                        onChange={(e) => patch({ responsible: e.target.value })}
                        placeholder="Namn/roll"
                      />
                    </div>
                    <div>
                      <Label>Allvarlighet</Label>
                      <Select
                        value={c.severity}
                        onValueChange={(v) => patch({ severity: v as ConflictRow["severity"] })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="låg">Låg</SelectItem>
                          <SelectItem value="medel">Medel</SelectItem>
                          <SelectItem value="hög">Hög</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center justify-between sm:col-span-2">
                      <Button
                        size="sm"
                        variant={c.resolved ? "secondary" : "default"}
                        onClick={() => patch({ resolved: !c.resolved })}
                      >
                        {c.resolved ? "Markerad som löst" : "Markera som löst"}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          setContent({
                            ...review.content_json,
                            conflicts: conflicts.filter((x) => x.id !== c.id),
                          })
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default ScheduleReview;
